import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { isReservedSupporterRouteSlug } from "../lib/supporterReservedRoutes";

type PublicIdeaResolve =
	| {
			active: true;
			roomId: string;
			voterPath: string;
			question?: string;
			phase?: string;
	  }
	| {
			active: false;
			message?: string;
			teamSlug?: string;
			ideaSlug?: string;
	  };

type LoadState =
	| { kind: "loading" }
	| { kind: "redirect"; to: string }
	| { kind: "inactive"; message: string }
	| { kind: "error"; message: string };

type ActivePublicIdea = Extract<PublicIdeaResolve, { active: true }>;

function isActivePublicIdea(data: unknown): data is ActivePublicIdea {
	return (
		typeof data === "object" &&
		data !== null &&
		"active" in data &&
		(data as { active: unknown }).active === true
	);
}

export function SupporterIdeaVoterPage() {
	const { teamSlug = "", ideaSlug = "" } = useParams();
	const [state, setState] = useState<LoadState>({ kind: "loading" });

	useEffect(() => {
		if (isReservedSupporterRouteSlug(teamSlug)) {
			return;
		}
		let cancelled = false;
		const run = async () => {
			if (!teamSlug || !ideaSlug) {
				setState({ kind: "error", message: "Missing team or Idea slug in the URL." });
				return;
			}
			setState({ kind: "loading" });
			try {
				const res = await fetch(
					`/api/public/teams/${encodeURIComponent(teamSlug)}/ideas/${encodeURIComponent(ideaSlug)}`,
					{ headers: { Accept: "application/json" } },
				);
				const data = (await res.json().catch(() => null)) as PublicIdeaResolve | { error?: string } | null;
				if (cancelled) return;
				if (!res.ok) {
					const err =
						data &&
						typeof data === "object" &&
						"error" in data &&
						typeof data.error === "string"
							? data.error
							: `Could not load this Idea (${res.status}).`;
					setState({ kind: "error", message: err });
					return;
				}
				if (isActivePublicIdea(data)) {
					const voterPath =
						data.voterPath.startsWith("/")
							? data.voterPath
							: data.roomId
								? `/r/${data.roomId}`
								: null;
					if (voterPath) {
						setState({ kind: "redirect", to: voterPath });
						return;
					}
				}
				const message =
					data && typeof data === "object" && "message" in data && typeof data.message === "string"
						? data.message
						: "No open Idea is running for this template right now.";
				setState({ kind: "inactive", message });
			} catch {
				if (!cancelled) {
					setState({ kind: "error", message: "Network error while loading this Idea." });
				}
			}
		};
		void run();
		return () => {
			cancelled = true;
		};
	}, [teamSlug, ideaSlug]);

	if (isReservedSupporterRouteSlug(teamSlug)) {
		return <Navigate to="/" replace />;
	}

	if (state.kind === "redirect") {
		return <Navigate to={state.to} replace />;
	}

	return (
		<div className="mx-auto max-w-lg space-y-6 px-4 py-16">
			<p className="font-medium text-wi-gold text-xs uppercase tracking-[0.18em]">Supporter Idea</p>
			<h1 className="font-display text-3xl text-wi-text tracking-tight">
				{teamSlug}/{ideaSlug}
			</h1>
			{state.kind === "loading" ? (
				<p className="text-sm text-wi-text/70">Finding the active voting room…</p>
			) : null}
			{state.kind === "inactive" ? (
				<p className="text-sm text-wi-text/75 leading-relaxed">{state.message}</p>
			) : null}
			{state.kind === "error" ? (
				<p className="rounded-xl border border-wi-gold/30 bg-wi-gold-soft px-4 py-3 text-sm text-wi-text" role="alert">
					{state.message}
				</p>
			) : null}
			<p className="text-sm text-wi-text/65">
				<Link to="/" className="font-medium text-wi-gold underline-offset-2 hover:underline">
					Back to home
				</Link>
			</p>
		</div>
	);
}
